/**
 * Composant Alert
 * Messages d'alerte avec icônes
 */

import { type HTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

const alertVariants = cva(
    'relative w-full rounded-lg border p-4 flex gap-3',
    {
        variants: {
            variant: {
                default: 'bg-white dark:bg-[#1E293B] border-[#E2E8F0] dark:border-[#334155] text-[#1E293B] dark:text-[#F1F5F9]',
                destructive: 'bg-[#FEF2F2] dark:bg-[#7F1D1D]/30 border-[#EF4444] text-[#DC2626] dark:text-[#F87171]',
                success: 'bg-[#F0FDF4] dark:bg-[#14532D]/30 border-[#10B981] text-[#10B981] dark:text-[#22C55E]',
                warning: 'bg-[#FFFBEB] dark:bg-[#78350F]/30 border-[#F59E0B] text-[#B45309] dark:text-[#FBBF24]',
                info: 'bg-[#EFF6FF] dark:bg-[#091A2E]/30 border-[#3B82F6] text-[#3B82F6] dark:text-[#60A5FA]',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

const alertIcons = {
    default: Info,
    destructive: AlertCircle,
    success: CheckCircle,
    warning: AlertTriangle,
    info: Info,
};

export interface AlertProps
    extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
    showIcon?: boolean;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
    ({ className, variant, showIcon = true, children, ...props }, ref) => {
        const Icon = alertIcons[variant || 'default'];

        return (
            <div
                ref={ref}
                role="alert"
                className={cn(alertVariants({ variant }), className)}
                {...props}
            >
                {showIcon && <Icon className="h-5 w-5 shrink-0 mt-0.5" />}
                <div className="flex-1">{children}</div>
            </div>
        );
    }
);

Alert.displayName = 'Alert';

const AlertTitle = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLHeadingElement>>(
    ({ className, ...props }, ref) => (
        <h5
            ref={ref}
            className={cn('font-medium leading-none tracking-tight', className)}
            {...props}
        />
    )
);

AlertTitle.displayName = 'AlertTitle';

const AlertDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn('text-sm mt-1 opacity-90', className)}
            {...props}
        />
    )
);

AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
